import CsvParser from 'papaparse'
import {
  BULK_DELETE_CSV_SIDE_HEADERS,
  BULK_DELETE_CSV_SKU_HEADER,
  BULK_DELETE_MAX_RECORDS,
  MAX_IMAGE_SIDE,
  MIN_IMAGE_SIDE,
} from '../constants'

// Slots 1-88 plus hero; slot 89 is reserved for the hero image and only accepted as "hero".
const SINGLE_SIDE_PATTERN =
  /^(?:[1-9]|[1-7]\d|8[0-8]|hero|xl1|lxl1|rxl1|bxl1|front|back|left|right)$/i

export function isValidBulkDeleteSide(side) {
  const raw = String(side ?? '').trim()
  if (!raw) {
    return false
  }

  const normalized = raw.toLowerCase()
  if (normalized === 'all') {
    return true
  }

  if (raw.includes(',')) {
    return false
  }

  return SINGLE_SIDE_PATTERN.test(raw)
}

/** Normalizes side for duplicate checks. */
export function normalizeBulkDeleteSideKey(side) {
  return String(side ?? '')
    .trim()
    .toLowerCase()
}

const ALLOWED_CSV_HEADERS = new Set([
  BULK_DELETE_CSV_SKU_HEADER,
  ...BULK_DELETE_CSV_SIDE_HEADERS,
])

export function normalizeHeader(header) {
  return String(header ?? '')
    .replace(/^\ufeff/, '')
    .trim()
    .toLowerCase()
}

export function normalizeCsvRow(row) {
  return Object.entries(row || {}).reduce((acc, [key, value]) => {
    acc[normalizeHeader(key)] = String(value ?? '').trim()
    return acc
  }, {})
}

export function getSideColumnKey(headers) {
  return BULK_DELETE_CSV_SIDE_HEADERS.find((column) => headers.includes(column))
}

function getSideColumnIndex(headerRow) {
  for (const column of BULK_DELETE_CSV_SIDE_HEADERS) {
    const index = headerRow.indexOf(column)
    if (index !== -1) {
      return index
    }
  }
  return -1
}

/**
 * Parses bulk-delete CSV text (exactly sku and side columns per row).
 */
export function parseBulkDeleteCsvContent(csvText) {
  const table = CsvParser.parse(String(csvText ?? '').trim(), {
    skipEmptyLines: true,
  }).data.filter((row) => row.some((cell) => String(cell ?? '').trim()))

  if (!table.length) {
    return {
      rows: [],
      error: { errorKey: 'catalogue.skuMediaUpload.bulkDelete.emptyCsv' },
    }
  }

  const headerRow = table[0].map((cell) => normalizeHeader(cell))
  const skuIndex = headerRow.indexOf(BULK_DELETE_CSV_SKU_HEADER)
  const sideIndex = getSideColumnIndex(headerRow)

  if (skuIndex === -1) {
    return {
      rows: [],
      error: {
        errorKey: 'catalogue.skuMediaUpload.bulkDelete.missingSkuColumn',
      },
    }
  }

  if (sideIndex === -1) {
    return {
      rows: [],
      error: {
        errorKey: 'catalogue.skuMediaUpload.bulkDelete.missingSideColumn',
      },
    }
  }

  if (headerRow.length !== 2) {
    const extraColumns = headerRow.filter(
      (header) => !ALLOWED_CSV_HEADERS.has(header)
    )
    return {
      rows: [],
      error: {
        errorKey: 'catalogue.skuMediaUpload.bulkDelete.invalidColumns',
        values: {
          columns:
            extraColumns.length > 0
              ? extraColumns.join(', ')
              : headerRow.join(', '),
        },
      },
    }
  }

  const dataRows = table.slice(1)
  for (let rowIndex = 0; rowIndex < dataRows.length; rowIndex += 1) {
    const cells = dataRows[rowIndex]
    const hasExtraColumn = cells.some(
      (cell, index) =>
        index !== skuIndex && index !== sideIndex && String(cell ?? '').trim()
    )

    if (hasExtraColumn) {
      return {
        rows: [],
        error: {
          errorKey: 'catalogue.skuMediaUpload.bulkDelete.invalidColumns',
          values: { columns: `row ${rowIndex + 2}` },
        },
      }
    }
  }

  const rows = dataRows.map((cells) => ({
    sku: String(cells[skuIndex] ?? '').trim(),
    side: String(cells[sideIndex] ?? '').trim(),
  }))

  return { rows, error: null }
}

export function buildBulkDeleteCsvContent(rows) {
  const lines = ['sku,side']

  rows.forEach(({ sku, side }) => {
    lines.push(`${sku},${side}`)
  })

  return lines.join('\n')
}

export function createBulkDeleteCsvFile(rows, fileName) {
  return new File([buildBulkDeleteCsvContent(rows)], fileName, {
    type: 'text/csv',
  })
}

export function validateBulkDeleteRows(rows) {
  if (!rows.length) {
    return { errorKey: 'catalogue.skuMediaUpload.bulkDelete.emptyCsv' }
  }

  if (rows.length > BULK_DELETE_MAX_RECORDS) {
    return {
      errorKey: 'catalogue.skuMediaUpload.bulkDelete.tooManyRows',
      values: { max: BULK_DELETE_MAX_RECORDS, count: rows.length },
    }
  }

  const seenRows = new Map()
  const duplicateRows = []

  for (let index = 0; index < rows.length; index += 1) {
    const sku = String(rows[index].sku ?? '').trim()
    const side = String(rows[index].side ?? '').trim()

    if (!sku || !side) {
      return {
        errorKey: 'catalogue.skuMediaUpload.bulkDelete.invalidRows',
        values: { row: index + 2 },
      }
    }

    if (!/^\d+$/.test(sku)) {
      return {
        errorKey: 'catalogue.skuMediaUpload.bulkDelete.invalidSku',
        values: { row: index + 2, sku },
      }
    }

    if (!isValidBulkDeleteSide(side)) {
      return {
        errorKey: 'catalogue.skuMediaUpload.bulkDelete.invalidSide',
        values: {
          row: index + 2,
          side,
          minSide: MIN_IMAGE_SIDE,
          maxSide: MAX_IMAGE_SIDE,
        },
      }
    }

    const rowKey = `${sku}|${normalizeBulkDeleteSideKey(side)}`
    if (seenRows.has(rowKey)) {
      duplicateRows.push({
        row: index + 2,
        firstRow: seenRows.get(rowKey),
      })
    } else {
      seenRows.set(rowKey, index + 2)
    }
  }

  if (duplicateRows.length > 0) {
    return {
      errorKey: 'catalogue.skuMediaUpload.bulkDelete.duplicateRows',
    }
  }

  return null
}

/** @deprecated Use parseBulkDeleteCsvContent + validateBulkDeleteRows */
export function validateBulkDeleteCsv(rows) {
  const normalizedRows = rows.map((row) => {
    const parsed = normalizeCsvRow(row)
    const sideColumn = getSideColumnKey(Object.keys(row).map(normalizeHeader))
    return {
      sku: parsed[BULK_DELETE_CSV_SKU_HEADER],
      side: parsed[sideColumn],
    }
  })

  return validateBulkDeleteRows(normalizedRows)
}
